import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material"
import React, { ChangeEvent, FormEvent, useContext, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import city from "../../../../assets/img/city.png"
import ReactLogoTTB from "../../../../assets/LogoTTB_green.svg"
import Right from "../../../../assets/Right Background.png"
import { customPalette } from "../../../../theme"
import { theme } from "../../../components/layout/BSLayout"
import { pagesUrl } from "../../../core/appConstants"
import { TokenContext } from "../../../core/context/user/token-context"
import { UserContext } from "../../../core/context/user/user-context"
import { useUser } from "../../../core/hooks/use-user"
import { getEnvProperty, PROPERTIES } from "../../../core/services/environment-service"

export default function SignInPage(): React.JSX.Element {
  const { getTokenFromCognito } = useContext(TokenContext)
  const { userLoading, getCurrentUserConnexion } = useContext(UserContext)
  const [credentials, setCredentials] = useState({ name: "", password: "" })
  const [error, setError] = useState<string>("")
  const { sendUserConnexionInformation } = useUser()

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const target = event.target
    const value = target.type === "checkbox" ? target.checked : target.value
    const name = target.id

    setError("")

    setCredentials({
      ...credentials,
      [name]: value,
    })
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    getTokenFromCognito(credentials.name.toLowerCase(), credentials.password)
      .then(() => {
        sendUserConnexionInformation()
      })
      .then(() => {
        getCurrentUserConnexion()
      })
      .catch((e: any) => {
        switch (e.code) {
          case "Username should be either an email or a phone number.":
          case "UserNotConfirmedException":
            setError(
              `Il faut confirmer cet utilisateur. Aller sur : ${getEnvProperty(PROPERTIES.REACT_APP_BASE_URL)}${
                pagesUrl.CONFIRM_PAGE
              }`
            )
            break
          case "NotAuthorizedException":
            setError("Login ou mot de passe incorrect")
            break
          case "UserNotFoundException":
            setError("Login ou mot de passe incorrect")
            break
          default:
            setError("Erreur lors de la connection")
            console.error("Code inconnu:", e)
        }
      })
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100vh",
          display: "flex",
          backgroundColor: customPalette.paperBackground,
        }}>
        <Box display="flex" flexDirection="column" width="70%" p={8} gap={8}>
          <Box component="img" src={ReactLogoTTB} width="35%" />

          <Box>
            <Typography variant="subtitle1" fontSize={24} fontWeight={600}>
              Ravi de vous voir
            </Typography>
            <Typography fontWeight={400} fontSize={19} color={customPalette.darkBlue3}>
              Estimez et pilotez le poids carbone de vos bâtiments
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center" width="100%">
            <Box component="img" src={city} height="100%" width="max-content" />
          </Box>
        </Box>

        <Box component="img" src={Right} sx={{ width: "30%", backgroundSize: "cover", backgroundPosition: "center" }} />

        <Box
          sx={{
            position: "absolute",
            display: "flex",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            flexDirection: "column",
            alignItems: "center",
            top: "50%",
            left: "70%",
            width: "25%",
            p: 4,
            borderRadius: 5,
            gap: 2,
            columnGap: 4,
            boxShadow: 7,
            background: customPalette.textPrimaryWhite,
          }}>
          <Typography variant="subtitle1" fontSize={24} fontWeight={600}>
            S'identifier
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              id="name"
              margin="normal"
              required
              fullWidth
              label="Email"
              value={credentials.name}
              onChange={handleChange}
              error={error !== ""}
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: 3,
                  },
                  "&:hover fieldset": {
                    borderRadius: 3,
                  },
                  "&.Mui-focused fieldset": {
                    borderRadius: 3,
                  },
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              id="password"
              margin="normal"
              type="password"
              required
              fullWidth
              label="Mot de passe"
              value={credentials.password}
              onChange={handleChange}
              error={error !== ""}
              sx={{
                borderRadius: 3,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: 3,
                  },
                  "&:hover fieldset": {
                    borderRadius: 3,
                  },
                  "&.Mui-focused fieldset": {
                    borderRadius: 3,
                  },
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormHelperText error>{error}</FormHelperText>

            <Grid container>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Link component={RouterLink} to={pagesUrl.FORGOT_PWD_PAGE}>
                  Mot de passe oublié ?
                </Link>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="center">
              {!userLoading ? (
                <Button type="submit" variant="contained" sx={{ mt: 3, mb: 2 }}>
                  Se Connecter
                </Button>
              ) : (
                <CircularProgress />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
